import {
  Box,
  ClickAwayListener,
  Divider,
  Grow,
  Paper,
  Popper,
  Typography,
} from "@mui/material";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

/**
 * A styled Popper-based dropdown menu with consistent rounded styling.
 *
 * Can be used standalone (controlled via ref) or as a building block.
 *
 * Usage (standalone with ref):
 *   const menuRef = useRef();
 *   <Button ref={anchorRef} onClick={() => menuRef.current.toggle()}>Open</Button>
 *   <StyledMenu ref={menuRef} anchorRef={anchorRef} title="My Menu">
 *     <MenuItem>...</MenuItem>
 *   </StyledMenu>
 *
 * Usage (controlled):
 *   <StyledMenu open={open} onClose={handleClose} anchorRef={anchorRef}>
 *     ...
 *   </StyledMenu>
 *
 * @param {object}   props
 * @param {object}   props.anchorRef       – React ref to the anchor element
 * @param {boolean}  [props.open]          – Controlled open state (optional, uses internal state if omitted)
 * @param {function} [props.onClose]       – Called when the menu should close
 * @param {function} [props.onOpen]        – Called when the menu opens
 * @param {string}   [props.title]         – Optional title at the top of the menu
 * @param {number}   [props.minWidth]      – Min width of the menu (default: 240)
 * @param {number}   [props.maxWidth]      – Max width of the menu
 * @param {string}   [props.placement]     – Popper placement (default: "bottom-end")
 * @param {React.ReactNode} props.children – Menu content
 * @param {object}   [props.sx]            – Additional sx for the Paper
 */
const StyledMenu = forwardRef(
  (
    {
      anchorRef,
      open: controlledOpen,
      onClose,
      onOpen,
      title,
      description,
      minWidth = 240,
      maxWidth,
      placement = "bottom-end",
      children,
      sx = {},
    },
    ref,
  ) => {
    const [internalOpen, setInternalOpen] = useState(false);

    // Support both controlled and uncontrolled modes
    const isControlled = controlledOpen !== undefined;
    const isOpen = isControlled ? controlledOpen : internalOpen;

    const handleOpen = useCallback(() => {
      if (!isControlled) setInternalOpen(true);
      onOpen?.();
    }, [isControlled, onOpen]);

    const handleClose = useCallback(
      (e) => {
        // Don't close if clicking the anchor element
        if (
          anchorRef?.current &&
          e?.target &&
          anchorRef.current.contains(e.target)
        ) {
          return;
        }
        if (!isControlled) setInternalOpen(false);
        onClose?.(e);
      },
      [isControlled, onClose, anchorRef],
    );

    const handleToggle = useCallback(() => {
      if (isOpen) {
        if (!isControlled) setInternalOpen(false);
        onClose?.();
      } else {
        handleOpen();
      }
    }, [isOpen, isControlled, onClose, handleOpen]);

    // Expose toggle/open/close to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        toggle: handleToggle,
        open: handleOpen,
        close: handleClose,
        isOpen,
      }),
      [handleToggle, handleOpen, handleClose, isOpen],
    );

    return (
      <Popper
        open={isOpen}
        anchorEl={anchorRef?.current}
        placement={placement}
        transition
        disablePortal={false}
        style={{ zIndex: 1300 }}
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, 6],
            },
          },
        ]}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: "right top" }}>
            <Paper
              elevation={3}
              sx={{
                minWidth,
                ...(maxWidth && { maxWidth }),
                borderRadius: "12px",
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                ...sx,
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  {title && (
                    <>
                      <Box sx={{ px: 2, pt: 1.75, pb: 1.25 }}>
                        {/* <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: "text.primary",
                          }}
                        >
                          {title}
                        </Typography> */}
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            fontSize: "11px",
                            color: "#6b7280",
                            textTransform: "uppercase",
                            letterSpacing: "0.06em",
                          }}
                        >
                          {title}
                        </Typography>
                        {description && (
                          <Typography
                            sx={{
                              fontSize: 12,
                              //   fontWeight: 700,
                              color: "text.secondary",
                            }}
                          >
                            {description}
                          </Typography>
                        )}
                      </Box>
                      <Divider />
                    </>
                  )}
                  {children}
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    );
  },
);

StyledMenu.displayName = "StyledMenu";

export default StyledMenu;
